import React from 'react';
import { Shield, Lock, Eye, Database, Globe, Mail, Phone, Building2, FileText, Users, Clock, ArrowRight, MapPin } from 'lucide-react';

const PrivacyPolicy: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-3">
                <Shield className="h-8 w-8 text-avn-purple-600" />
                <h1 className="text-3xl font-bold">Privacy Policy</h1>
              </div>
              <div className="text-sm text-gray-500">
                <p>Effective Date: 7 April 2025</p>
                <p>Last Updated: 7 April 2025</p>
              </div>
            </div>

            <div className="prose max-w-none">
              <p className="text-gray-600 mb-6">
                This Privacy Policy explains how Agriculture Value Chain Network Ltd ("AVN", "we", "our", or "us") collects, uses, shares, and protects your personal data when you use our website and services. We are fully committed to protecting your privacy and ensuring compliance with the UK GDPR, EU GDPR, and the Data Protection Act 2018.
              </p>
              
              <section className="mb-6">
                <h2 className="text-2xl font-semibold mb-4">1. Who We Are</h2>
                <div className="flex items-start gap-3 bg-gray-50 p-4 rounded-lg mb-4">
                  <Building2 className="h-5 w-5 text-avn-purple-600 mt-1" />
                  <div>
                    <p className="font-medium">Agriculture Value Chain Network Ltd (AVN)</p>
                    <p className="text-gray-600">Company Number: 14482448</p>
                    <p className="text-gray-600">Registered Office: 128 City Road, London, EC1V 2NX, United Kingdom</p>
                    <p className="text-gray-600">Email: info@avnearth.com</p>
                    <p className="text-gray-600">Website: www.AVNearth.com</p>
                  </div>
                </div>
                <p>
                  AVN is an ethical agriculture and technology platform working across the UK, East Africa, and the US to support sustainable, regenerative farming and fair food systems.
                </p>
              </section>

              <section className="mb-6">
                <h2 className="text-2xl font-semibold mb-4">2. Information We Collect</h2>
                <div className="mb-4">
                  <h3 className="font-medium mb-2">a. Information You Provide</h3>
                  <ul className="list-disc pl-5 space-y-1 text-gray-600">
                    <li>Full name</li>
                    <li>Email address</li>
                    <li>Phone number</li>
                    <li>Farm or cooperative details</li>
                    <li>Location (e.g. village, region)</li>
                    <li>Comments, messages, or support requests</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-medium mb-2">b. Information We Collect Automatically</h3>
                  <ul className="list-disc pl-5 space-y-1 text-gray-600">
                    <li>IP address</li>
                    <li>Browser type and device ID</li>
                    <li>Access times and pages visited</li>
                    <li>Cookies and usage tracking (see [Cookie Policy])</li>
                  </ul>
                </div>
              </section>
              
              <section className="mb-6">
                <h2 className="text-2xl font-semibold mb-4">3. Legal Basis for Processing</h2>
                <p className="mb-3">We process your data on the following lawful grounds:</p>
                <ul className="list-disc pl-5 space-y-1 text-gray-600">
                  <li>Consent – when you've given us permission</li>
                  <li>Contract – when data is needed to deliver our services</li>
                  <li>Legal obligation – where required by law</li>
                  <li>Legitimate interest – for analytics, security, or service improvement</li>
                </ul>
              </section>

              <section className="mb-6">
                <h2 className="text-2xl font-semibold mb-4">4. How We Use Your Data</h2>
                <p className="mb-3">We use your data to:</p>
                <ul className="list-disc pl-5 space-y-1 text-gray-600">
                  <li>Register you on the AVN platform</li>
                  <li>Provide support and access to AVN tools and services</li>
                  <li>Respond to inquiries and feedback</li>
                  <li>Send relevant updates (with your consent)</li>
                  <li>Analyse platform use and improve performance</li>
                  <li>Protect the platform from fraud or misuse</li>
                </ul>
              </section>

              <section className="mb-6">
                <h2 className="text-2xl font-semibold mb-4">5. Sharing Your Data</h2>
                <p className="mb-3">We will never sell your data. We may share it with:</p>
                <ul className="list-disc pl-5 space-y-1 text-gray-600 mb-3">
                  <li>Local AVN partner hubs (e.g., in Uganda or Kenya)</li>
                  <li>Trusted service providers who support our platform</li>
                  <li>Regulatory bodies or legal authorities if required</li>
                </ul>
                <p className="text-gray-600">
                  Data may be transferred outside the UK with appropriate safeguards, including Standard Contractual Clauses.
                </p>
              </section>

              <section className="mb-6">
                <h2 className="text-2xl font-semibold mb-4">6. Your Rights</h2>
                <p className="mb-3">Under GDPR, you have the right to:</p>
                <div className="grid md:grid-cols-2 gap-x-4 gap-y-2">
                  <div className="flex items-center gap-2">
                    <ArrowRight className="h-4 w-4 text-avn-purple-600" />
                    <span className="text-gray-600">Access your personal data</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <ArrowRight className="h-4 w-4 text-avn-purple-600" />
                    <span className="text-gray-600">Correct any inaccuracies</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <ArrowRight className="h-4 w-4 text-avn-purple-600" />
                    <span className="text-gray-600">Delete your data ("right to be forgotten")</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <ArrowRight className="h-4 w-4 text-avn-purple-600" />
                    <span className="text-gray-600">Restrict or object to processing</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <ArrowRight className="h-4 w-4 text-avn-purple-600" />
                    <span className="text-gray-600">Withdraw consent at any time</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <ArrowRight className="h-4 w-4 text-avn-purple-600" />
                    <span className="text-gray-600">Request data portability</span>
                  </div>
                </div>
                <p className="mt-3 text-gray-600">
                  To exercise your rights, contact us at info@avnearth.com
                </p>
              </section>

              <section className="mb-6">
                <h2 className="text-2xl font-semibold mb-4">7. Data Retention</h2>
                <div className="flex items-start gap-3">
                  <Clock className="h-5 w-5 text-avn-purple-600 mt-1" />
                  <p className="text-gray-600">
                    We only retain your data for as long as needed to provide our services or as required by law. When no longer needed, your data will be securely deleted or anonymised.
                  </p>
                </div>
              </section>

              <section className="mb-6">
                <h2 className="text-2xl font-semibold mb-4">8. Security</h2>
                <p className="mb-3">We use industry-standard security practices to protect your information:</p>
                <ul className="list-disc pl-5 space-y-1 text-gray-600">
                  <li>SSL encryption</li>
                  <li>Secure cloud storage</li>
                  <li>Role-based access control</li>
                  <li>Regular security checks and updates</li>
                </ul>
              </section>

              <section className="mb-6">
                <h2 className="text-2xl font-semibold mb-4">9. Cookies and Tracking</h2>
                <p className="text-gray-600">
                  We use cookies to optimise your experience and analyse traffic. You can manage cookie preferences through your browser. Read more in our [Cookie Policy].
                </p>
              </section>

              <section className="mb-6">
                <h2 className="text-2xl font-semibold mb-4">10. Children's Privacy</h2>
                <div className="flex items-start gap-3">
                  <Users className="h-5 w-5 text-avn-purple-600 mt-1" />
                  <p className="text-gray-600">
                    AVN is not intended for use by children under 16. We do not knowingly collect their data.
                  </p>
                </div>
              </section>

              <section className="mb-6">
                <h2 className="text-2xl font-semibold mb-4">11. Changes to This Policy</h2>
                <p className="text-gray-600">
                  We may update this policy from time to time. Any updates will be posted here and marked with a new "Effective Date."
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold mb-4">12. Contact Us</h2>
                <p className="mb-3">For privacy-related questions, complaints, or to exercise your rights:</p>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Building2 className="h-5 w-5 text-avn-purple-600" />
                    <span className="font-medium">Agriculture Value Chain Network Ltd</span>
                  </div>
                  <div className="flex items-center gap-2 mb-2">
                    <Mail className="h-5 w-5 text-avn-purple-600" />
                    <span>Email: info@avnearth.com</span>
                  </div>
                  <div className="flex items-center gap-2 mb-2">
                    <MapPin className="h-5 w-5 text-avn-purple-600" />
                    <span>Registered Office: 128 City Road, London, EC1V 2NX, United Kingdom</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Globe className="h-5 w-5 text-avn-purple-600" />
                    <span>Website: www.AVNearth.com</span>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;